import React from 'react';
import PropTypes from 'prop-types';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_RED,
} from 'client/components/SrErrorMessage/SrErrorMessage';
import UpPmsUnitTypesPicker from 'client/components/UPPmsUnitTypes/UpPmsUnitTypesPicker';

// Presentational shell shared by the Rent Manager and Rentvine connect flows in the self
// onboarding Connect step. The two flows differ only in copy/provider/state wiring — the JSX
// (failed error message, connected pill, "Test connection" button, and the housing-types
// picker mount) is identical, so it lives here and is parameterized by props. The provider's
// test handler (`onTest`) still owns all flow-specific logic (RM test-then-upsert vs Rentvine
// upsert-validates-credentials); this component never calls the API itself.
const UpPmsConnectSection = ({
  provider,
  connectionStatus,
  errorMessage,
  fallbackErrorMessage,
  connectedLabel,
  integration,
  pickerRef,
  onTest,
  onSaved,
  utilityPartner,
  theme,
  className,
}) => (
  <SrBox margin="0 0 16px" className={className}>
    {errorMessage && connectionStatus === 'failed' && (
      <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_RED} marginBottom="16px">
        {errorMessage}
      </SrErrorMessage>
    )}
    {!errorMessage && connectionStatus === 'failed' && (
      <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_RED} marginBottom="16px">
        {fallbackErrorMessage}
      </SrErrorMessage>
    )}
    {connectionStatus === 'connected' && (
      <SrBox marginBottom="16px">
        <SrText font="greycliff" weight="800" color="green">
          {connectedLabel}
        </SrText>
      </SrBox>
    )}
    {connectionStatus !== 'connected' && (
      <SrButton
        theme={SR_BUTTON_THEME_ADOBE_GREEN}
        hasBoxShadow
        type="button"
        disabled={connectionStatus === 'testing'}
        onClick={onTest}
        themeOverrides={{
          backgroundColor: theme?.buttonPrimaryColor,
          color: theme?.buttonPrimaryTextColor,
        }}
      >
        {connectionStatus === 'testing' ? 'Testing…' : 'Test connection'}
      </SrButton>
    )}
    {integration && (
      <SrBox marginTop="16px">
        <UpPmsUnitTypesPicker
          ref={pickerRef}
          utilityPartner={utilityPartner}
          integration={integration}
          provider={provider}
          onSaved={onSaved}
        />
      </SrBox>
    )}
  </SrBox>
);

UpPmsConnectSection.displayName = 'UpPmsConnectSection';

UpPmsConnectSection.propTypes = {
  provider: PropTypes.string.isRequired,
  connectionStatus: PropTypes.string,
  errorMessage: PropTypes.string,
  fallbackErrorMessage: PropTypes.node.isRequired,
  connectedLabel: PropTypes.node.isRequired,
  integration: PropTypes.object,
  pickerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onTest: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  utilityPartner: PropTypes.object.isRequired,
  theme: PropTypes.object,
  className: PropTypes.string,
};

export default UpPmsConnectSection;
